import React, { useEffect, useRef, useState } from 'react';
import matterportService from '../services/matterportService';
import audioService from '../services/audioService';
import signageService from '../services/signageService';
import navBillboardService from '../services/navBillboardService';
import navDirectionSignService from '../services/navDirectionSignService';
import { MATTERPORT_CONFIG, PROJECT_CONFIG } from '../config/projectConfig';
import { useLoading } from '../context/LoadingContext';
import { useLanguage } from '../context/LanguageContext';
import { TIMING, Z_INDEX } from '../const';
import TourOverlay from './TourOverlay';
import TourPanOverlay from './TourPanOverlay';
import LocationQuickNav from './LocationQuickNav';
import TempleHeader from './TempleHeader';
import InfoPanel from './InfoPanel';
import LinksPanel from './LinksPanel';
import LocationGallery from './LocationGallery';
import LocationImageOverlay from './LocationImageOverlay';
import SignageEditor from './SignageEditor';
import NavigationHints from './NavigationHints';
import NavHintsEditor from './NavHintsEditor';
import QuickNavEditor from './QuickNavEditor';
import preloadImages from '../utils/preloadImages';
import quickNavConfig from '../config/quickNavConfig.json';
import './WelcomeModal.css';

const WELCOME_TEXT = {
  mr: {
    title: 'सुस्वागतम्',
    subtitle: 'मंगळ ग्रह मंदिर, अमळनेर येथे आपले स्वागत आहे!',
    welcome: 'या पवित्र मंदिराच्या व्हर्च्युअल दर्शनात आपले स्वागत आहे. कृपया मंदिरातील दिव्य स्थळांचा अनुभव घ्या आणि शांत व समृद्ध आध्यात्मिक अनुभूतीचा आनंद घ्या। ✨',
    enter: 'दर्शन सुरू करा',
    loading: 'कृपया प्रतीक्षा करा…',
  },
  en: {
    title: 'A Warm Welcome',
    subtitle: 'Mangal Grah Mandir, Amalner welcomes you.',
    welcome: 'Welcome to the virtual tour of this sacred temple. Step in to explore its divine spaces and immerse yourself in a serene and enriching spiritual experience.',
    enter: 'Start Tour',
    loading: 'Preparing your tour…',
  },
  hi: {
    title: 'सुस्वागतम्',
    subtitle: 'मंगल ग्रह मंदिर, अमलनेर में आपका स्वागत है!',
    welcome: 'इस पवित्र मंदिर के वर्चुअल भ्रमण में आपका स्वागत है। कृपया मंदिर के दिव्य स्थलों का अनुभव करें और एक शांत एवं आध्यात्मिक अनुभूति का आनंद लें। ✨',
    enter: 'दर्शन शुरू करें',
    loading: 'कृपया प्रतीक्षा करें…',
  },
};

// Detect iOS Safari + small Android phones. iOS Safari is particularly
// aggressive about reloading tabs that exceed its per-tab memory budget;
// on those devices we defer mounting the heavy Matterport iframe until
// AFTER the user taps Start Tour, so the welcome screen and the 3D scene
// don't compete for memory at the same time (which is what causes the
// "tap Start Tour → tab silently reloads" bug on iPhone).
const _isMobileDevice = () => {
  if (typeof window === 'undefined' || typeof navigator === 'undefined') return false;
  try {
    const ua = navigator.userAgent || '';
    if (/Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini|Mobile|Tablet/i.test(ua)) return true;
    const hasTouch = ('ontouchstart' in window) || (navigator.maxTouchPoints || 0) > 1;
    if (hasTouch && Math.min(window.innerWidth, window.innerHeight) <= 820) return true;
  } catch (_) { /* noop */ }
  return false;
};

const MatterportViewer = () => {
  const iframeRef = useRef(null);
  const { isLoaded, setIsLoaded } = useLoading();
  const { language, setLanguage } = useLanguage();
  // Mobile path: defer mounting the Matterport iframe (and therefore
  // audioService.loadConfig) until the user taps Start Tour. This is
  // required so the muted-autoplay call inside loadConfig() that primes
  // the ambient music element happens INSIDE the user-gesture window
  // — iOS Chrome / Safari otherwise block the eventual unmute.
  // Desktop / kiosk mounts immediately so the SDK warms up during the
  // welcome screen.
  const [iframeMounted, setIframeMounted] = useState(() => !_isMobileDevice());
  const [showInfoPanel, setShowInfoPanel] = useState(false);
  const [showLinksPanel, setShowLinksPanel] = useState(false);
  // When true, LinksPanel opens directly into the Live Darshan embed
  // instead of the videos grid (header has separate Live / Videos buttons).
  const [linksAutoLive, setLinksAutoLive] = useState(false);
  const [showWelcome, setShowWelcome] = useState(true);
  const [isNavigating, setIsNavigating] = useState(false);
  const [tourRunning, setTourRunning] = useState(false);
  const [drone360Active, setDrone360Active] = useState(false);
  const [navFade, setNavFade] = useState(false);

  // Preload progress for footer quick-nav thumbnails, welcome logo,
  // and other startup imagery. We hold the Start Tour button until
  // BOTH the SDK has loaded AND the preload pass has finished — so
  // tapping Start Tour never lands on a footer with grey placeholders.
  const [preloadDone, setPreloadDone] = useState(false);

  // Kick off image preload exactly once on mount.
  // Critical-path only: footer thumbnails (used immediately on Start
  // Tour) plus the Welcome screen assets. Drone / panorama images are
  // loaded lazily on first interaction so the user reaches
  // the experience faster.
  useEffect(() => {
    const stops = (quickNavConfig && quickNavConfig.locations) || [];
    const stopThumbs = stops
      .map((s) => s?.thumb || s?.image)
      .filter(Boolean);
    // On mobile we skip the large MANDIR AREA.jpeg welcome background
    // from the preload set — it's a multi-MB JPEG that stays decoded in
    // memory and contributes to iOS Safari's per-tab memory pressure
    // right when the Matterport WebGL scene is uploading textures.
    const isMobile = _isMobileDevice();
    const startupImages = [
      `${process.env.PUBLIC_URL}/assets/images/mandir-logo.png`,
      ...(isMobile ? [] : [`${process.env.PUBLIC_URL}/assets/images/MANDIR AREA.jpeg`]),
      ...stopThumbs,
    ];
    preloadImages(startupImages).then(() => setPreloadDone(true));

    // Kick off ambient audio config load IMMEDIATELY on mount — on mobile
    // the iframe (and therefore matterportService.connect → fetchAndLoadConfig)
    // is deferred until the user taps Start Tour, which means the ambient
    // MP3 only starts buffering AFTER the tap and audible playback is
    // delayed by 5+ seconds. Loading the config here lets audioService
    // create the streaming <audio> element and begin a muted autoplay
    // during the welcome screen, so playback is effectively instant on
    // tap. matterportService later calls fetchAndLoadConfig again — that
    // simply re-fetches the same JSON (cheap) and does not duplicate
    // the audio element.
    audioService.fetchAndLoadConfig('/config/locationAudioConfig.json').catch((err) => {
      console.warn('🌿 Early ambient config load failed:', err?.message);
    });
  }, []);

  // Mobile path: the user has tapped Start Tour, so the iframe has been
  // mounted and audio is unlocked, but we kept the welcome screen up
  // showing the spinner. Once the SDK finishes loading, dismiss the
  // welcome automatically — there's no second tap to make.
  useEffect(() => {
    if (isLoaded && iframeMounted && showWelcome && _isMobileDevice()) {
      setShowWelcome(false);
    }
  }, [isLoaded, iframeMounted, showWelcome]);

  // Listen for nav transitions so we can paint our own black overlay
  // synchronously with the service's teleport. This gives us full control
  // over when the screen uncovers — in particular, it stays black until
  // after zoom is applied so the user never sees zoom animate.
  // Events whose detail.cover === false (guided-tour FLY transitions) are
  // signal-only — they let POIs hide during the walk but skip the black
  // overlay so the user feels they are walking between stops.
  useEffect(() => {
    const onStart = (e) => {
      if (e?.detail?.cover === false) return;
      setNavFade(true);
    };
    const onEnd = () => setNavFade(false);
    window.addEventListener('navTransitionStart', onStart);
    window.addEventListener('navTransitionEnd', onEnd);
    return () => {
      window.removeEventListener('navTransitionStart', onStart);
      window.removeEventListener('navTransitionEnd', onEnd);
    };
  }, []);

  // Track guided-tour run state → used to block iframe interaction below.
  // Also: when the tour finishes naturally (last stop reached), wait 10s
  // and snap the experience back to the welcome / starting view so a kiosk
  // session resets itself if the visitor walks away.
  useEffect(() => {
    let resetTimer = null;
    const onStep = () => {
      setTourRunning(true);
      if (resetTimer) { clearTimeout(resetTimer); resetTimer = null; }
    };
    const onStop = () => setTourRunning(false);
    const onComplete = () => {
      if (resetTimer) clearTimeout(resetTimer);
      // Small breath so the last narration's tail isn't cut off, then
      // close the drone view and fly back to the starting sweep. Do NOT
      // re-show the welcome modal — visitor stays inside the experience
      // at the entrance, ready to explore again.
      resetTimer = setTimeout(() => {
        window.dispatchEvent(new CustomEvent('close360View'));
        setShowInfoPanel(false);
        setShowLinksPanel(false);
        matterportService.resetToStart().catch(() => {});
        resetTimer = null;
      }, 800);
    };
    matterportService.addEventListener('tourStep', onStep);
    matterportService.addEventListener('tourStop', onStop);
    matterportService.addEventListener('tourComplete', onComplete);
    return () => {
      matterportService.removeEventListener('tourStep', onStep);
      matterportService.removeEventListener('tourStop', onStop);
      matterportService.removeEventListener('tourComplete', onComplete);
      if (resetTimer) clearTimeout(resetTimer);
    };
  }, []);

  useEffect(() => {
    const onOpen  = () => setDrone360Active(true);
    const onClose = () => setDrone360Active(false);
    window.addEventListener('drone360Open',  onOpen);
    window.addEventListener('drone360Close', onClose);
    return () => {
      window.removeEventListener('drone360Open',  onOpen);
      window.removeEventListener('drone360Close', onClose);
    };
  }, []);

  // Update signage labels when language changes
  useEffect(() => {
    signageService.setLanguage(language);
    navBillboardService.setLanguage(language);
    navDirectionSignService.setLanguage(language);
  }, [language]);

  useEffect(() => {
    const onStart = () => setIsNavigating(true);
    const onEnd   = () => setIsNavigating(false);
    window.addEventListener('navigationStart', onStart);
    window.addEventListener('navigationEnd',   onEnd);
    return () => {
      window.removeEventListener('navigationStart', onStart);
      window.removeEventListener('navigationEnd',   onEnd);
    };
  }, []);

  const handleSkyView = () => {
    // Stop guided tour if active (skip return — sky view takes over)
    matterportService.stopTour({ skipReturn: true });
    window.dispatchEvent(new CustomEvent('open360View'));
  };

  const handleReset = async () => {
    try {
      window.dispatchEvent(new CustomEvent('close360View'));
      setShowInfoPanel(false);
      setShowLinksPanel(false);
      setShowWelcome(true);
      // Navigate back to the starting sweep in the background
      matterportService.resetToStart().catch(() => {});
    } catch (e) { console.error(e); }
  };

  useEffect(() => {
    const currentIframe = iframeRef.current;
    let initialized = false;

    const initializeMatterport = async () => {
      if (initialized || !currentIframe) return;
      initialized = true;

      const success = await matterportService.initialize(currentIframe, currentIframe.contentWindow);
      
      if (success) {
        setIsLoaded(true);

        // Start auto-navigation after a short delay to ensure SDK is fully ready
        setTimeout(async () => {
          try {
            await matterportService.autoNavigateOnStart();
          } catch (error) {
            console.error('Failed to auto-navigate:', error);
          }
        }, TIMING.MATTERPORT.AUTO_NAVIGATE_DELAY);
      }
    };

    // Inject brand-hide + SDK-loading-overlay-hide CSS into the iframe.
    // The SDK replaces its own document head during boot, so a single
    // injection on `load` is not enough — we keep a MutationObserver
    // running for the lifetime of the iframe and re-inject whenever the
    // <head> changes (so a force-reload after iOS WebGL recovery still
    // suppresses the "Connecting to the Matterport space" overlay).
    const STYLE_ID = 'matterport-viewer-overrides';
    const CSS_TEXT = `
      .footer-ui .showcase-footer .footer-logo,
      .footer-ui .showcase-footer .logo-link,
      .powered-by,
      [class*="powered-by"],
      [class*="poweredBy"],
      .splash-logo,
      [class*="splash-logo"] {
        display: none !important;
        visibility: hidden !important;
        opacity: 0 !important;
      }

      /* Hide the SDK's "Connecting to the Matterport space" overlay
         (id=sdk-landing-container, .spinner-container + .message).
         Our own LoadingContext overlay covers this state already. */
      #sdk-landing-container,
      #sdk-landing-container .message,
      #sdk-landing-container .spinner-container,
      [id*="sdk-landing"],
      [class*="sdk-landing"],
      [class*="sdkLanding"] {
        display: none !important;
        visibility: hidden !important;
        opacity: 0 !important;
        pointer-events: none !important;
      }
    `;

    let mutationObserver = null;
    const injectOverrides = () => {
      try {
        const doc = currentIframe?.contentDocument || currentIframe?.contentWindow?.document;
        if (!doc) return;
        if (!doc.getElementById(STYLE_ID)) {
          const style = doc.createElement('style');
          style.id = STYLE_ID;
          style.setAttribute('data-injected-by', 'matterport-viewer');
          style.textContent = CSS_TEXT;
          (doc.head || doc.documentElement).appendChild(style);
        }
        // (Re)attach observer to the new document if needed.
        if (mutationObserver) mutationObserver.disconnect();
        mutationObserver = new MutationObserver(() => {
          if (!doc.getElementById(STYLE_ID)) {
            const style = doc.createElement('style');
            style.id = STYLE_ID;
            style.setAttribute('data-injected-by', 'matterport-viewer');
            style.textContent = CSS_TEXT;
            (doc.head || doc.documentElement).appendChild(style);
          }
        });
        mutationObserver.observe(doc.documentElement, { childList: true, subtree: true });
      } catch (_) { /* cross-origin or not ready */ }
    };

    // Handle iframe load event
    const handleIframeLoad = () => {
      // Always (re-)inject the override style on every iframe load — even
      // if SDK init already ran once, because a force-reload (e.g. iOS
      // Safari WebGL recovery) replaces the iframe document and wipes
      // any previously-injected styles.
      injectOverrides();
      if (initialized) return;
      setTimeout(initializeMatterport, TIMING.MATTERPORT.INIT_DELAY);
    };

    if (currentIframe) {
      currentIframe.addEventListener('load', handleIframeLoad);
    }

    return () => {
      if (currentIframe) {
        currentIframe.removeEventListener('load', handleIframeLoad);
      }
      if (mutationObserver) {
        mutationObserver.disconnect();
        mutationObserver = null;
      }
    };
  }, [setIsLoaded, iframeMounted]);

  // iOS Safari can lose its WebGL context when the tab spends a long time
  // backgrounded, returning to a frozen/black canvas. The CORRECT signal
  // for this is the `webglcontextlost` event on the iframe's WebGL canvas.
  // The previous implementation reloaded on every visibilitychange — that
  // re-triggered the SDK's "Connecting to the Matterport space" overlay
  // every time the user briefly switched apps, even when the canvas was
  // perfectly healthy.
  //
  // We now only reload when context loss is observed. Mobile-only — desktop
  // browsers don't trigger this code path at all.
  useEffect(() => {
    if (!iframeMounted) return;
    const ua = navigator.userAgent || '';
    const isIOS = /iPad|iPhone|iPod/.test(ua) && !window.MSStream;
    if (!isIOS) return;

    const iframe = iframeRef.current;
    if (!iframe) return;

    let attached = false;
    let canvasRef = null;
    const onContextLost = (e) => {
      e?.preventDefault?.();
      console.warn('🛑 WebGL context lost on iframe canvas — reloading iframe');
      try {
        const src = iframe.src;
        if (src && src !== 'about:blank') iframe.src = src;
      } catch (_) { /* ignore */ }
    };

    // Poll briefly for the canvas to appear inside the iframe document
    // (SDK creates it asynchronously after init). Once attached, the
    // listener stays for the iframe lifetime.
    let attempts = 0;
    const attachInterval = setInterval(() => {
      attempts++;
      if (attached || attempts > 60) { // give up after ~30s
        clearInterval(attachInterval);
        return;
      }
      try {
        const doc = iframe.contentDocument || iframe.contentWindow?.document;
        const canvas = doc?.querySelector('canvas');
        if (canvas) {
          canvas.addEventListener('webglcontextlost', onContextLost, { passive: false });
          canvasRef = canvas;
          attached = true;
          clearInterval(attachInterval);
        }
      } catch (_) { /* cross-origin or not ready */ }
    }, 500);

    return () => {
      clearInterval(attachInterval);
      try {
        canvasRef?.removeEventListener('webglcontextlost', onContextLost);
      } catch (_) { /* ignore */ }
    };
  }, [iframeMounted]);

  return (
    <div className="relative w-full h-full" style={{ width: '100%', height: '100%' }}>
      {/* Welcome screen shown from first load; spinner replaces the button until SDK is ready */}
      {showWelcome && (
        <div
          role="dialog"
          aria-modal="true"
          aria-labelledby="welcome-screen-title"
          className="absolute inset-0 welcome-modal-overlay"
          style={{ zIndex: Z_INDEX.LOADER, animation: 'none', position: 'absolute' }}
        >
          {/* Blurred temple photo background */}
          <div
            aria-hidden="true"
            className="welcome-modal-bg"
            style={{ backgroundImage: `url('${process.env.PUBLIC_URL}/assets/images/MANDIR AREA.jpeg')` }}
          />
          {/* Cream gradient overlay */}
          <div className="welcome-modal-gradient" />

          <div className="welcome-modal-content">
            <img
              aria-hidden="true"
              className="welcome-modal-om"
              src={`${process.env.PUBLIC_URL}/assets/images/mandir-logo.png`}
              alt=""
            />

            <div className="welcome-modal-header">
              <h1 id="welcome-screen-title" className="welcome-modal-title">{(WELCOME_TEXT[language] || WELCOME_TEXT.mr).title}</h1>
              <h2 className="welcome-modal-subtitle">{(WELCOME_TEXT[language] || WELCOME_TEXT.mr).subtitle}</h2>
              <div className="welcome-modal-divider">
                <span className="welcome-modal-divider-line welcome-modal-divider-line--full" />
              </div>
            </div>

            <p className="welcome-modal-description">
              {(WELCOME_TEXT[language] || WELCOME_TEXT.mr).welcome}
            </p>

            {/* Show a spinner until the Matterport SDK is fully loaded
                AND startup images are preloaded; only then reveal the
                Start Tour button so tapping it never lands on a blank
                canvas or a footer of placeholder thumbnails. The tap is
                also the mobile audio-unlock gesture.

                On mobile we DEFER mounting the iframe until after the
                tap so the audio config load (and its muted-autoplay
                primer for ambient music) happens inside the user-
                gesture window. After the tap we keep the welcome
                screen visible with the spinner until isLoaded fires. */}
            {((iframeMounted ? isLoaded : true) && preloadDone) ? (
              <button
                type="button"
                className="welcome-modal-enter"
                onClick={() => {
                  try { audioService.unlockOnGesture?.(); } catch (_) {}
                  audioService.setGlobalVolume(0.7);
                  // Open the audio gate so ambient + spatial sources may
                  // start now (they are deliberately suppressed during
                  // scene loading so the user enters silence-first).
                  try { audioService.allowStart?.(); } catch (_) {}
                  // Mobile path: mount the iframe now (loadConfig +
                  // muted-autoplay run inside this gesture stack) and
                  // keep the welcome screen visible with the spinner
                  // until isLoaded fires — then the auto-dismiss
                  // effect below removes it.
                  if (!iframeMounted) {
                    setIframeMounted(true);
                    return;
                  }
                  setShowWelcome(false);
                }}
              >
                {(WELCOME_TEXT[language] || WELCOME_TEXT.mr).enter}
              </button>
            ) : (
              <span
                className="welcome-modal-spinner"
                role="status"
                aria-label="Loading tour"
              />
            )}

            <div className="welcome-modal-lang">
              {PROJECT_CONFIG.i18n.supportedLanguages.map((lang) => (
                <button
                  key={lang.code}
                  type="button"
                  onClick={() => setLanguage(lang.code)}
                  className={`welcome-modal-lang-btn ${lang.code === language ? 'active' : ''}`}
                >
                  {lang.label}
                </button>
              ))}
            </div>
          </div>

          <style>{`
            @keyframes snakeSpin { to { transform: rotate(360deg); } }
          `}</style>
        </div>
      )}
      <iframe
        ref={iframeRef}
        title="Matterport"
        src={iframeMounted ? MATTERPORT_CONFIG.showcase_url : 'about:blank'}
        allow="autoplay; fullscreen; web-share; xr-spatial-tracking; gyroscope; accelerometer; camera; microphone; vr; ar;"
        className={`w-full h-full absolute inset-0 transition-opacity duration-1000 ${
          isLoaded ? 'opacity-100' : 'opacity-0'
        }`}
        style={{
          width: '100%',
          height: '100%',
          border: 'none',
          outline: 'none',
          // Hide the iframe until it's actually mounted so it doesn't
          // intercept taps on the welcome screen.
          visibility: iframeMounted ? 'visible' : 'hidden',
        }}
        frameBorder="0"
        loading="eager"
        referrerPolicy="no-referrer-when-downgrade"
        scrolling="no"
      />
      {isLoaded && <TourOverlay />}
      {/* Our own black fade overlay — painted in sync with navigation
          events so the screen stays black until zoom is fully applied.
          Sits above iframe, below floating UI (tour bar, editors). */}
      <div
        aria-hidden="true"
        style={{
          position: 'absolute', inset: 0,
          background: '#000',
          opacity: navFade ? 1 : 0,
          transition: navFade ? 'opacity 180ms ease-in' : 'opacity 300ms ease-out',
          pointerEvents: navFade ? 'auto' : 'none',
          zIndex: Z_INDEX.TOUR_SLIDESHOW - 1,
        }}
      />
      {/* Block iframe interaction during path navigation */}
      {isNavigating && (
        <div
          aria-hidden="true"
          style={{
            position: 'absolute', inset: 0,
            zIndex: Z_INDEX.LOADER - 1,
            cursor: 'wait',
          }}
        />
      )}
      {/* Block iframe interaction while the guided tour is running so
          the user can't grab the camera mid-tour. Floating controls
          (stop/pause/next) live at higher z-index and remain clickable.

          Mobile exception: users on touch devices may pan/tilt the
          camera (look around) but still cannot navigate to a different
          sweep. We capture touches on the overlay and translate
          single-finger drags into Camera.rotate(...) calls; taps and
          multi-touch gestures are swallowed (which would otherwise
          trigger sweep navigation / zoom).

          Drone exception: when the sky-view pano is open (e.g. last
          tour step), the drone surface owns its own input — neither
          overlay should sit on top of it. (Doing so on mobile also
          triggered iOS Safari WebGL crashes because every drag was
          still rotating the underlying Matterport camera.) */}
      {tourRunning && !drone360Active && (
        <TourPanOverlay />
      )}
      {isLoaded && !showWelcome && <LocationQuickNav onSkyView={handleSkyView} />}
      {isLoaded && !showWelcome && (
        <TempleHeader
          language={language}
          onReset={handleReset}
          onInfo={() => setShowInfoPanel(true)}
          onLinks={() => { setLinksAutoLive(false); setShowLinksPanel(true); }}
          onLive={() => { setLinksAutoLive(true); setShowLinksPanel(true); }}
        />
      )}
      {showInfoPanel  && <InfoPanel  onClose={() => setShowInfoPanel(false)} />}
      {showLinksPanel && (
        <LinksPanel
          autoOpenLive={linksAutoLive}
          onClose={() => { setShowLinksPanel(false); setLinksAutoLive(false); }}
        />
      )}
      {isLoaded && !showWelcome && <LocationGallery />}
      {isLoaded && !showWelcome && <LocationImageOverlay />}
      {isLoaded && <NavigationHints active={!showWelcome} />}
      {/* Hide the POI and Guided-Tour editors while the drone sky view is
          open — those editors act on the Matterport scene, not the 360
          panorama, so they'd be useless there. */}
      {isLoaded && <NavHintsEditor active={!showWelcome && !drone360Active} />}
      {isLoaded && <QuickNavEditor active={!showWelcome && !drone360Active} />}
      {isLoaded && !showWelcome && process.env.REACT_APP_SIGNAGE_EDITOR === 'true' && <SignageEditor />}
    </div>
  );
};

export default MatterportViewer; 